import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FIELDS, FIELD_GROUPS, FIELD_BY_KEY, PRESETS, columnsFromPreset, makeColumn, neededLookups } from './exportFields';
import { CONCURRENCY, PAGE_SIZE, buildTable, countOrders, daysInRange, downloadTable, fetchOrders } from './exportEngine';

/**
 * Export wizard - Live View and Accounts downloads.
 *
 *   1. Filters  - date range (order date or assigned date), partner, legs, payment, statuses,
 *                 with a live count of how many orders that is.
 *   2. Columns  - presets (Operations / Accounts / Everything / last used), search, add/remove,
 *                 reorder and rename columns.
 *   3. Export   - pulls the range a day at a time (see exportEngine), shows progress, retries, and
 *                 only then builds the file. A day that cannot be loaded is reported, never skipped
 *                 silently; the file is not auto-downloaded while anything is missing.
 */
const STATUS_OPTIONS = [
  'UNASSIGNED', 'ASSIGNED', 'PICKUP_ENROUTE', 'PICKED_UP', 'INSCAN_AT_WAREHOUSE', 'DESTINATION_LINEHAUL_DEPART',
  'DROP_ENROUTE', 'DELIVERED', 'FAILED', 'RETURN_TO_SHIPPER', 'CANCELLED', 'READY', 'PLANNED',
];

const read = (k, fb) => { try { const v = localStorage.getItem(k); return v == null ? fb : JSON.parse(v); } catch (e) { return fb; } };
const write = (k, v) => { try { localStorage.setItem(k, JSON.stringify(v)); } catch (e) { /* not persisted */ } };

const pad = (n) => String(n).padStart(2, '0');
const iso = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const QUICK_RANGES = [
  ['Today', () => { const d = new Date(); return [iso(d), iso(d)]; }],
  ['Yesterday', () => { const d = new Date(); d.setDate(d.getDate() - 1); return [iso(d), iso(d)]; }],
  ['Last 7 days', () => { const t = new Date(); const f = new Date(); f.setDate(t.getDate() - 6); return [iso(f), iso(t)]; }],
  ['Last 30 days', () => { const t = new Date(); const f = new Date(); f.setDate(t.getDate() - 29); return [iso(f), iso(t)]; }],
  ['This month', () => { const t = new Date(); return [iso(new Date(t.getFullYear(), t.getMonth(), 1)), iso(t)]; }],
  ['Last month', () => { const t = new Date(); return [iso(new Date(t.getFullYear(), t.getMonth() - 1, 1)), iso(new Date(t.getFullYear(), t.getMonth(), 0))]; }],
];
const fmtNum = (n) => (n == null ? '—' : Number(n).toLocaleString());
const fmtDuration = (ms) => {
  if (ms == null || !Number.isFinite(ms)) return '—';
  const s = Math.max(0, Math.round(ms / 1000));
  return s >= 90 ? `${Math.floor(s / 60)}m ${s % 60}s` : `${s}s`;
};

const CSS = `
.exw-backdrop { position:fixed; inset:0; background:rgba(15,23,42,.55); z-index:3000; display:flex; align-items:center;
  justify-content:center; padding:16px; }
.exw { background:#fff; border-radius:18px; width:100%; max-width:1040px; max-height:calc(100vh - 32px); display:flex;
  flex-direction:column; box-shadow:0 24px 64px rgba(15,23,42,.35); text-align:left; color:#0f172a;
  font-family:'Poppins',-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif; overflow:hidden; }
.exw-head { padding:18px 22px 14px; border-bottom:1px solid #eef2f7; display:flex; align-items:flex-start;
  justify-content:space-between; gap:12px; }
.exw-title { font-size:18px; font-weight:700; margin:0; }
.exw-sub { font-size:12.5px; color:#64748b; margin-top:3px; }
.exw-x { border:none; background:#f1f5f9; width:34px; height:34px; border-radius:10px; cursor:pointer; color:#475569; font-size:18px; flex-shrink:0; }
.exw-steps { display:flex; gap:8px; padding:12px 22px; border-bottom:1px solid #eef2f7; background:#f8fafc; flex-wrap:wrap; }
.exw-step { display:flex; align-items:center; gap:8px; font-size:12.5px; font-weight:600; color:#94a3b8; border:none;
  background:none; padding:4px 6px; border-radius:8px; cursor:pointer; font-family:inherit; }
.exw-step[disabled] { cursor:default; }
.exw-step .n { width:24px; height:24px; border-radius:50%; display:inline-flex; align-items:center; justify-content:center;
  background:#e2e8f0; color:#475569; font-size:12px; font-weight:700; }
.exw-step.on { color:#1e3a8a; } .exw-step.on .n { background:#1e3a8a; color:#fff; }
.exw-step.done { color:#15803d; } .exw-step.done .n { background:#dcfce7; color:#15803d; }
.exw-body { padding:18px 22px; overflow:auto; flex:1; }
.exw-foot { padding:14px 22px; border-top:1px solid #eef2f7; display:flex; justify-content:space-between; align-items:center;
  gap:10px; flex-wrap:wrap; background:#fff; }
.exw-btn { height:38px; padding:0 16px; border-radius:10px; border:1px solid #cbd5e1; background:#fff; color:#0f172a;
  font-weight:600; font-size:13px; cursor:pointer; display:inline-flex; align-items:center; gap:8px; font-family:inherit; }
.exw-btn:disabled { opacity:.5; cursor:not-allowed; }
.exw-btn.primary { background:#1e3a8a; border-color:#1e3a8a; color:#fff; }
.exw-btn.primary:hover:not(:disabled) { background:#1e40af; }
.exw-btn.danger { color:#b91c1c; border-color:#fecaca; }
.exw-btn.ghost { border-color:transparent; color:#475569; }
.exw-grid { display:grid; grid-template-columns:minmax(0, 1.5fr) minmax(0, 1fr); gap:18px; }
.exw-card { border:1px solid #e2e8f0; border-radius:14px; padding:14px 16px; background:#fff; }
.exw-label { font-size:11.5px; font-weight:700; color:#475569; text-transform:uppercase; letter-spacing:.05em; margin-bottom:8px; display:block; }
.exw-field { margin-bottom:16px; }
.exw-input { height:38px; border:1px solid #cbd5e1; border-radius:10px; padding:0 10px; font-size:13px; font-family:inherit;
  color:#0f172a; background:#fff; outline:none; min-width:0; }
.exw-input:focus { border-color:#2563eb; box-shadow:0 0 0 3px rgba(37,99,235,.12); }
.exw-row { display:flex; gap:10px; align-items:center; flex-wrap:wrap; }
.exw-seg { display:inline-flex; border:1px solid #cbd5e1; border-radius:10px; overflow:hidden; flex-wrap:wrap; }
.exw-seg button { border:none; background:#fff; padding:8px 14px; font-size:12.5px; font-weight:600; color:#475569; cursor:pointer;
  font-family:inherit; border-right:1px solid #e2e8f0; }
.exw-seg button:last-child { border-right:none; }
.exw-seg button.on { background:#1e3a8a; color:#fff; }
.exw-chips { display:flex; flex-wrap:wrap; gap:6px; }
.exw-chip { border:1px solid #e2e8f0; background:#fff; border-radius:999px; padding:5px 11px; font-size:12px; font-weight:600;
  color:#475569; cursor:pointer; font-family:inherit; }
.exw-chip.on { border-color:#2563eb; background:#eff6ff; color:#1d4ed8; }
.exw-hint { font-size:12px; color:#64748b; margin-top:6px; }
.exw-est { background:linear-gradient(135deg,#eff6ff,#eef2ff); border:1px solid #c7d2fe; border-radius:14px; padding:18px; }
.exw-est-n { font-size:34px; font-weight:800; color:#1e3a8a; line-height:1.1; font-variant-numeric:tabular-nums; }
.exw-est-l { font-size:13px; color:#3730a3; font-weight:600; }
.exw-est-meta { font-size:12px; color:#475569; margin-top:10px; line-height:1.7; }
.exw-presets { display:grid; grid-template-columns:repeat(auto-fit, minmax(190px, 1fr)); gap:10px; margin-bottom:16px; }
.exw-preset { border:1px solid #e2e8f0; border-radius:12px; padding:12px; background:#fff; cursor:pointer; text-align:left;
  font-family:inherit; display:flex; gap:10px; align-items:flex-start; }
.exw-preset:hover { border-color:#93c5fd; background:#f8fbff; }
.exw-preset .ic { width:34px; height:34px; border-radius:10px; background:#eff6ff; color:#1d4ed8; display:flex; align-items:center;
  justify-content:center; flex-shrink:0; }
.exw-preset b { font-size:13.5px; display:block; color:#0f172a; }
.exw-preset span { font-size:11.5px; color:#64748b; }
.exw-cols { display:grid; grid-template-columns:minmax(0, 1fr) minmax(0, 1fr); gap:16px; }
.exw-pane { border:1px solid #e2e8f0; border-radius:14px; overflow:hidden; display:flex; flex-direction:column; min-height:0; }
.exw-pane-h { padding:10px 14px; background:#f8fafc; border-bottom:1px solid #eef2f7; display:flex; justify-content:space-between;
  align-items:center; gap:8px; font-size:12px; font-weight:700; color:#475569; }
.exw-pane-b { overflow:auto; max-height:420px; padding:6px 0; }
.exw-group-h { display:flex; justify-content:space-between; align-items:center; padding:8px 14px 4px; font-size:11px; font-weight:700;
  color:#94a3b8; text-transform:uppercase; letter-spacing:.05em; }
.exw-group-h button { border:none; background:none; color:#2563eb; font-size:11px; font-weight:700; cursor:pointer; font-family:inherit; }
.exw-opt { display:flex; align-items:center; gap:10px; padding:6px 14px; font-size:13px; cursor:pointer; }
.exw-opt:hover { background:#f8fafc; }
.exw-opt input { width:16px; height:16px; accent-color:#1e3a8a; }
.exw-col { display:grid; grid-template-columns:24px minmax(0,1fr) auto; gap:8px; align-items:center; padding:5px 10px 5px 12px; }
.exw-col:hover { background:#f8fafc; }
.exw-col .idx { font-size:11px; color:#94a3b8; text-align:right; font-variant-numeric:tabular-nums; }
.exw-col input { height:30px; border:1px solid transparent; border-radius:8px; padding:0 8px; font-size:13px; font-family:inherit;
  width:100%; background:transparent; color:#0f172a; }
.exw-col input:hover { border-color:#e2e8f0; background:#fff; }
.exw-col input:focus { border-color:#2563eb; background:#fff; outline:none; }
.exw-col .src { font-size:10.5px; color:#94a3b8; padding:0 8px; }
.exw-icon { border:none; background:none; color:#64748b; width:26px; height:26px; border-radius:8px; cursor:pointer; }
.exw-icon:hover:not(:disabled) { background:#e2e8f0; color:#0f172a; }
.exw-icon:disabled { opacity:.3; cursor:default; }
.exw-bar { height:12px; border-radius:999px; background:#e2e8f0; overflow:hidden; }
.exw-bar i { display:block; height:100%; background:linear-gradient(90deg,#2563eb,#4f46e5); border-radius:999px; transition:width .3s ease; }
.exw-stats { display:grid; grid-template-columns:repeat(4, minmax(0,1fr)); gap:10px; margin-top:14px; }
.exw-stat { background:#f8fafc; border-radius:12px; padding:10px 12px; }
.exw-stat b { display:block; font-size:20px; font-variant-numeric:tabular-nums; }
.exw-stat span { font-size:11.5px; color:#64748b; font-weight:600; }
.exw-alert { border-radius:12px; padding:12px 14px; font-size:13px; margin-top:14px; }
.exw-alert.ok { background:#f0fdf4; border:1px solid #bbf7d0; color:#166534; }
.exw-alert.warn { background:#fffbeb; border:1px solid #fde68a; color:#92400e; }
.exw-alert.err { background:#fef2f2; border:1px solid #fecaca; color:#991b1b; }
.exw-summary { display:grid; grid-template-columns:repeat(auto-fit, minmax(190px, 1fr)); gap:10px; margin-bottom:16px; }
.exw-summary div { background:#f8fafc; border-radius:12px; padding:10px 12px; font-size:12px; color:#64748b; }
.exw-summary b { display:block; color:#0f172a; font-size:13px; margin-top:2px; overflow-wrap:anywhere; }
@media (max-width: 860px) {
  .exw-grid, .exw-cols { grid-template-columns:1fr; }
  .exw-stats { grid-template-columns:repeat(2, minmax(0,1fr)); }
}
@media (max-width: 560px) {
  .exw-backdrop { padding:0; }
  .exw { max-height:100vh; height:100vh; border-radius:0; }
  .exw-body { padding:14px; } .exw-head, .exw-foot, .exw-steps { padding-left:14px; padding-right:14px; }
}
`;

export default function ExportWizard({
  open, onClose, context = 'liveview', title = 'Export orders', partners = [],
  initialFilters = {}, defaultPreset = 'operations', statusOptions = STATUS_OPTIONS,
}) {
  const API_URL = process.env.REACT_APP_API_URL;
  const colsKey = `exportWizard.columns.${context}`;
  const fmtKey = 'exportWizard.format';

  const [step, setStep] = useState(1);
  const [filters, setFilters] = useState({});
  const [columns, setColumns] = useState([]);
  const [search, setSearch] = useState('');
  const [fileName, setFileName] = useState('');
  const [format, setFormat] = useState(() => read(fmtKey, 'excel'));
  const [totals, setTotals] = useState(false);
  const [estimate, setEstimate] = useState({ loading: false, count: null, error: '' });
  const [phase, setPhase] = useState('idle'); // idle | running | done | cancelled
  const [progress, setProgress] = useState(null);
  const [result, setResult] = useState(null); // { rowsByDay, failedDays }
  const [startedAt, setStartedAt] = useState(null);
  const abortRef = useRef(null);
  const savedLayout = read(colsKey, null);

  // (Re)initialise every time the wizard opens, from the page's current filters.
  useEffect(() => {
    if (!open) return;
    const today = iso(new Date());
    setStep(1);
    setFilters({
      fromDate: initialFilters.fromDate || today,
      toDate: initialFilters.toDate || initialFilters.fromDate || today,
      dateBasis: initialFilters.dateBasis || 'ORDER_DATE',
      partnerId: initialFilters.partnerId && String(initialFilters.partnerId) !== '0' ? String(initialFilters.partnerId) : '0',
      legType: initialFilters.legType || 'DROP',
      statuses: initialFilters.statuses || [],
      payment: initialFilters.payment || '',
    });
    const preset = PRESETS.find((p) => p.id === defaultPreset) || PRESETS[0];
    const saved = read(colsKey, null);
    setColumns(Array.isArray(saved) && saved.length
      ? saved.filter(([k]) => FIELD_BY_KEY[k]).map(([k, l]) => makeColumn(k, l))
      : columnsFromPreset(preset));
    setTotals(!!preset.totals);
    setSearch('');
    setPhase('idle');
    setProgress(null);
    setResult(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const days = useMemo(() => daysInRange(filters.fromDate, filters.toDate), [filters.fromDate, filters.toDate]);
  const rangeError = !filters.fromDate || !filters.toDate ? 'Pick both dates'
    : filters.toDate < filters.fromDate ? '“To” is before “From”'
      : days.length > 366 ? 'Pick at most one year at a time' : '';

  const partnerName = (pid) => {
    if (!pid || String(pid) === '0') return 'All partners';
    const p = partners.find((x) => String(x.pid) === String(pid));
    return p ? `${String(p.pid).padStart(2, '0')} - ${p.pname}` : `Partner ${pid}`;
  };

  // Live estimate - one COUNT on the server, debounced while the filters are being edited.
  useEffect(() => {
    if (!open || step !== 1 || rangeError) return undefined;
    const ctl = new AbortController();
    setEstimate((e) => ({ ...e, loading: true, error: '' }));
    const t = setTimeout(async () => {
      try {
        const n = await countOrders(API_URL, filters, ctl.signal);
        setEstimate({ loading: false, count: n, error: n == null ? 'Estimate unavailable' : '' });
      } catch (e) {
        if (e?.code !== 'ERR_CANCELED') setEstimate({ loading: false, count: null, error: 'Estimate unavailable - you can still export' });
      }
    }, 450);
    return () => { clearTimeout(t); ctl.abort(); };
  }, [open, step, filters, rangeError, API_URL]);

  // Default file name follows the filters until the user types their own.
  const [nameTouched, setNameTouched] = useState(false);
  useEffect(() => {
    if (nameTouched || !filters.fromDate) return;
    const who = filters.partnerId && filters.partnerId !== '0' ? `-p${filters.partnerId}` : '';
    const range = filters.fromDate === filters.toDate ? filters.fromDate : `${filters.fromDate}_to_${filters.toDate}`;
    setFileName(`${context === 'accounts' ? 'accounts' : 'live-view'}${who}-${range}`);
  }, [filters, context, nameTouched]);

  const setF = (patch) => setFilters((f) => ({ ...f, ...patch }));
  const toggleStatus = (s) => setF({ statuses: filters.statuses.includes(s) ? filters.statuses.filter((x) => x !== s) : [...filters.statuses, s] });

  // ── columns ────────────────────────────────────────────────────────────────────────────────
  const chosenKeys = useMemo(() => new Set(columns.map((c) => c.key)), [columns]);
  const toggleField = (key) => setColumns((cols) => (cols.some((c) => c.key === key)
    ? cols.filter((c) => c.key !== key) : [...cols, makeColumn(key)]));
  const setGroup = (group, on) => setColumns((cols) => {
    const keys = FIELDS.filter((f) => f.group === group).map((f) => f.key);
    if (!on) return cols.filter((c) => !keys.includes(c.key));
    const have = new Set(cols.map((c) => c.key));
    return [...cols, ...keys.filter((k) => !have.has(k)).map((k) => makeColumn(k))];
  });
  const move = (i, d) => setColumns((cols) => {
    const j = i + d;
    if (j < 0 || j >= cols.length) return cols;
    const next = [...cols];
    [next[i], next[j]] = [next[j], next[i]];
    return next;
  });
  const rename = (id, label) => setColumns((cols) => cols.map((c) => (c.id === id ? { ...c, label } : c)));
  const removeCol = (id) => setColumns((cols) => cols.filter((c) => c.id !== id));
  const applyPreset = (p) => { setColumns(columnsFromPreset(p)); setTotals(!!p.totals); };
  const q = search.trim().toLowerCase();
  const visibleFields = FIELDS.filter((f) => !q || f.label.toLowerCase().includes(q) || f.group.toLowerCase().includes(q));

  // ── export ─────────────────────────────────────────────────────────────────────────────────
  const include = useMemo(() => neededLookups(columns), [columns]);

  const orderedRows = useCallback((rowsByDay) => {
    const seen = new Set();
    const out = [];
    days.forEach((d) => (rowsByDay[d] || []).forEach((r) => {
      const k = r.orderId != null ? `o${r.orderId}` : `a${r.awb}`;
      if (!seen.has(k)) { seen.add(k); out.push(r); }
    }));
    return out;
  }, [days]);

  const saveFile = useCallback((rowsByDay, incomplete) => {
    const table = buildTable(orderedRows(rowsByDay), columns, { totals });
    downloadTable(table, { format, fileName: incomplete ? `${fileName}-INCOMPLETE` : fileName });
  }, [orderedRows, columns, totals, format, fileName]);

  const run = async (onlyDays, base) => {
    const ctl = new AbortController();
    abortRef.current = ctl;
    write(colsKey, columns.map((c) => [c.key, c.label]));
    write(fmtKey, format);
    setPhase('running');
    setStartedAt(Date.now());
    setProgress({ daysDone: 0, daysTotal: onlyDays.length, rows: 0, pages: 0, activeDays: [], failed: 0 });
    const res = await fetchOrders({ apiUrl: API_URL, days: onlyDays, filters, include, signal: ctl.signal, onProgress: setProgress });
    if (ctl.signal.aborted) { setPhase('cancelled'); return; }
    const merged = {
      rowsByDay: { ...(base?.rowsByDay || {}), ...res.rowsByDay },
      failedDays: res.failedDays,
    };
    setResult(merged);
    setPhase('done');
    if (!merged.failedDays.length) saveFile(merged.rowsByDay, false);
  };

  const cancel = () => { if (abortRef.current) abortRef.current.abort(); };
  const close = () => {
    if (phase === 'running' && !window.confirm('An export is still running. Stop it and close?')) return;
    cancel();
    onClose && onClose();
  };
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') close(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  if (!open) return null;

  const rowsSoFar = progress?.rows || 0;
  const elapsed = startedAt ? Date.now() - startedAt : 0;
  const rate = elapsed > 0 ? rowsSoFar / (elapsed / 1000) : 0;
  const pct = progress && progress.daysTotal ? Math.round((progress.daysDone / progress.daysTotal) * 100) : 0;
  const etaMs = estimate.count != null && rate > 0 ? ((estimate.count - rowsSoFar) / rate) * 1000
    : progress && progress.daysDone > 0 ? (elapsed / progress.daysDone) * (progress.daysTotal - progress.daysDone) : null;
  const totalRows = result ? orderedRows(result.rowsByDay).length : 0;
  const canNext1 = !rangeError;
  const canExport = columns.length > 0 && !rangeError;

  return (
    <div className="exw-backdrop" role="dialog" aria-modal="true" aria-label={title} onMouseDown={(e) => { if (e.target === e.currentTarget) close(); }}>
      <style>{CSS}</style>
      <div className="exw">
        <div className="exw-head">
          <div>
            <h2 className="exw-title"><i className="fa fa-file-excel-o" style={{ color: '#15803d', marginRight: 8 }} />{title}</h2>
            <div className="exw-sub">Pulled a day at a time and checked before the file is built - nothing is skipped.</div>
          </div>
          <button className="exw-x" onClick={close} aria-label="Close">×</button>
        </div>

        <div className="exw-steps">
          {[[1, 'Filters'], [2, 'Columns'], [3, 'Export']].map(([n, label]) => (
            <button key={n} type="button" className={`exw-step ${step === n ? 'on' : step > n ? 'done' : ''}`}
              disabled={phase === 'running' || (n > 1 && !canNext1) || (n === 3 && !canExport)}
              onClick={() => setStep(n)}>
              <span className="n">{step > n ? <i className="fa fa-check" /> : n}</span>{label}
            </button>
          ))}
        </div>

        <div className="exw-body">
          {step === 1 && (
            <div className="exw-grid">
              <div>
                <div className="exw-field">
                  <span className="exw-label">Date range</span>
                  <div className="exw-row">
                    <input type="date" className="exw-input" value={filters.fromDate || ''} onChange={(e) => setF({ fromDate: e.target.value })} aria-label="From date" />
                    <span style={{ color: '#94a3b8' }}>→</span>
                    <input type="date" className="exw-input" value={filters.toDate || ''} onChange={(e) => setF({ toDate: e.target.value })} aria-label="To date" />
                  </div>
                  <div className="exw-chips" style={{ marginTop: 8 }}>
                    {QUICK_RANGES.map(([label, fn]) => (
                      <button key={label} type="button" className="exw-chip" onClick={() => { const [f, t] = fn(); setF({ fromDate: f, toDate: t }); }}>{label}</button>
                    ))}
                  </div>
                  {rangeError && <div className="exw-hint" style={{ color: '#b91c1c' }}>{rangeError}</div>}
                </div>

                <div className="exw-field">
                  <span className="exw-label">Dates are</span>
                  <div className="exw-seg">
                    <button type="button" className={filters.dateBasis === 'ORDER_DATE' ? 'on' : ''} onClick={() => setF({ dateBasis: 'ORDER_DATE' })}>Order date</button>
                    <button type="button" className={filters.dateBasis === 'ASSIGNED_DATE' ? 'on' : ''} onClick={() => setF({ dateBasis: 'ASSIGNED_DATE' })}>Assigned date</button>
                  </div>
                  <div className="exw-hint">
                    {filters.dateBasis === 'ORDER_DATE'
                      ? 'Every order in the range, assigned to a driver or not.'
                      : 'Only orders assigned to a driver in the range - unassigned orders are left out.'}
                  </div>
                </div>

                <div className="exw-row" style={{ alignItems: 'flex-start', gap: 18 }}>
                  <div className="exw-field" style={{ flex: '1 1 220px' }}>
                    <span className="exw-label">Partner</span>
                    <select className="exw-input" style={{ width: '100%' }} value={filters.partnerId || '0'} onChange={(e) => setF({ partnerId: e.target.value })}>
                      <option value="0">All partners</option>
                      {[...partners].sort((a, b) => String(a.pname).localeCompare(String(b.pname))).map((p) => (
                        <option key={p.pid} value={p.pid}>{String(p.pid).padStart(2, '0')} - {p.pname}</option>
                      ))}
                    </select>
                  </div>
                  <div className="exw-field">
                    <span className="exw-label">Orders</span>
                    <div className="exw-seg">
                      {[['DROP', 'Deliveries'], ['PICK_UP', 'Pickups'], ['ALL', 'Both']].map(([v, l]) => (
                        <button key={v} type="button" className={filters.legType === v ? 'on' : ''} onClick={() => setF({ legType: v })}>{l}</button>
                      ))}
                    </div>
                  </div>
                  <div className="exw-field">
                    <span className="exw-label">Payment</span>
                    <div className="exw-seg">
                      {[['', 'All'], ['COD', 'COD'], ['NON_COD', 'Non-COD']].map(([v, l]) => (
                        <button key={l} type="button" className={filters.payment === v ? 'on' : ''} onClick={() => setF({ payment: v })}>{l}</button>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="exw-field">
                  <span className="exw-label">Status {filters.statuses?.length ? `(${filters.statuses.length})` : ''}</span>
                  <div className="exw-chips">
                    <button type="button" className={`exw-chip ${!filters.statuses?.length ? 'on' : ''}`} onClick={() => setF({ statuses: [] })}>All statuses</button>
                    {statusOptions.map((s) => (
                      <button key={s} type="button" className={`exw-chip ${filters.statuses?.includes(s) ? 'on' : ''}`} onClick={() => toggleStatus(s)}>
                        {s.replace(/_/g, ' ').toLowerCase().replace(/^./, (c) => c.toUpperCase())}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              <div>
                <div className="exw-est">
                  <div className="exw-est-l">This export</div>
                  <div className="exw-est-n">
                    {estimate.loading ? <i className="fa fa-spinner fa-spin" style={{ fontSize: 26 }} /> : fmtNum(estimate.count)}
                  </div>
                  <div className="exw-est-l">orders</div>
                  <div className="exw-est-meta">
                    <div><i className="fa fa-calendar" style={{ width: 16 }} /> {days.length} day{days.length === 1 ? '' : 's'} · {filters.dateBasis === 'ASSIGNED_DATE' ? 'by assigned date' : 'by order date'}</div>
                    <div><i className="fa fa-building-o" style={{ width: 16 }} /> {partnerName(filters.partnerId)}</div>
                    <div><i className="fa fa-exchange" style={{ width: 16 }} /> about {fmtNum(Math.max(days.length, Math.ceil((estimate.count || 0) / PAGE_SIZE)))} small requests, {CONCURRENCY} at a time</div>
                  </div>
                  {estimate.error && <div className="exw-hint" style={{ color: '#92400e' }}>{estimate.error}</div>}
                  {estimate.count === 0 && !estimate.loading && <div className="exw-hint" style={{ color: '#92400e' }}>No orders match these filters.</div>}
                </div>
              </div>
            </div>
          )}

          {step === 2 && (
            <div>
              <div className="exw-presets">
                {PRESETS.map((p) => (
                  <button key={p.id} type="button" className="exw-preset" onClick={() => applyPreset(p)}>
                    <span className="ic"><i className={`fa ${p.icon}`} /></span>
                    <span><b>{p.name}</b><span>{p.description}</span></span>
                  </button>
                ))}
                {Array.isArray(savedLayout) && savedLayout.length > 0 && (
                  <button type="button" className="exw-preset"
                    onClick={() => setColumns(savedLayout.filter(([k]) => FIELD_BY_KEY[k]).map(([k, l]) => makeColumn(k, l)))}>
                    <span className="ic" style={{ background: '#f0fdf4', color: '#15803d' }}><i className="fa fa-history" /></span>
                    <span><b>My last export</b><span>{savedLayout.length} columns, as you left them</span></span>
                  </button>
                )}
              </div>

              <div className="exw-cols">
                <div className="exw-pane">
                  <div className="exw-pane-h">
                    <input className="exw-input" style={{ height: 32, flex: 1 }} placeholder="Search fields…" value={search} onChange={(e) => setSearch(e.target.value)} />
                  </div>
                  <div className="exw-pane-b">
                    {FIELD_GROUPS.map((g) => {
                      const fs = visibleFields.filter((f) => f.group === g);
                      if (!fs.length) return null;
                      const allOn = fs.every((f) => chosenKeys.has(f.key));
                      return (
                        <div key={g}>
                          <div className="exw-group-h"><span>{g}</span>
                            <button type="button" onClick={() => setGroup(g, !allOn)}>{allOn ? 'None' : 'All'}</button>
                          </div>
                          {fs.map((f) => (
                            <label key={f.key} className="exw-opt">
                              <input type="checkbox" checked={chosenKeys.has(f.key)} onChange={() => toggleField(f.key)} />
                              <span>{f.label}</span>
                            </label>
                          ))}
                        </div>
                      );
                    })}
                    {!visibleFields.length && <div className="exw-hint" style={{ padding: '10px 14px' }}>No field matches “{search}”.</div>}
                  </div>
                </div>

                <div className="exw-pane">
                  <div className="exw-pane-h">
                    <span>Columns in the file ({columns.length})</span>
                    <button type="button" className="exw-btn ghost" style={{ height: 28, padding: '0 8px', fontSize: 12 }} onClick={() => setColumns([])} disabled={!columns.length}>Clear</button>
                  </div>
                  <div className="exw-pane-b">
                    {columns.map((c, i) => (
                      <div key={c.id} className="exw-col">
                        <span className="idx">{i + 1}</span>
                        <div style={{ minWidth: 0 }}>
                          <input value={c.label} onChange={(e) => rename(c.id, e.target.value)} aria-label={`Header for ${FIELD_BY_KEY[c.key]?.label}`} />
                          {c.label !== FIELD_BY_KEY[c.key]?.label && <div className="src">{FIELD_BY_KEY[c.key]?.label}</div>}
                        </div>
                        <div style={{ display: 'flex' }}>
                          <button type="button" className="exw-icon" title="Move up" disabled={i === 0} onClick={() => move(i, -1)}><i className="fa fa-arrow-up" /></button>
                          <button type="button" className="exw-icon" title="Move down" disabled={i === columns.length - 1} onClick={() => move(i, 1)}><i className="fa fa-arrow-down" /></button>
                          <button type="button" className="exw-icon" title="Remove" onClick={() => removeCol(c.id)}><i className="fa fa-times" /></button>
                        </div>
                      </div>
                    ))}
                    {!columns.length && <div className="exw-hint" style={{ padding: '18px 14px', textAlign: 'center' }}>Pick a preset or tick fields on the left.</div>}
                  </div>
                </div>
              </div>
            </div>
          )}

          {step === 3 && (
            <div>
              <div className="exw-summary">
                <div>Dates<b>{filters.fromDate === filters.toDate ? filters.fromDate : `${filters.fromDate} → ${filters.toDate}`}</b></div>
                <div>Partner<b>{partnerName(filters.partnerId)}</b></div>
                <div>Orders<b>{{ DROP: 'Deliveries', PICK_UP: 'Pickups', ALL: 'Deliveries + pickups' }[filters.legType]}{filters.payment ? ` · ${filters.payment === 'COD' ? 'COD' : 'Non-COD'}` : ''}</b></div>
                <div>Status<b>{filters.statuses?.length ? filters.statuses.join(', ') : 'All'}</b></div>
                <div>Columns<b>{columns.length}</b></div>
              </div>

              {phase === 'idle' || phase === 'cancelled' ? (
                <div className="exw-card">
                  <div className="exw-row" style={{ gap: 16, alignItems: 'flex-end' }}>
                    <div style={{ flex: '1 1 260px' }}>
                      <span className="exw-label">File name</span>
                      <input className="exw-input" style={{ width: '100%' }} value={fileName} onChange={(e) => { setNameTouched(true); setFileName(e.target.value); }} />
                    </div>
                    <div>
                      <span className="exw-label">Format</span>
                      <div className="exw-seg">
                        <button type="button" className={format === 'excel' ? 'on' : ''} onClick={() => setFormat('excel')}><i className="fa fa-file-excel-o" /> Excel</button>
                        <button type="button" className={format === 'csv' ? 'on' : ''} onClick={() => setFormat('csv')}><i className="fa fa-file-text-o" /> CSV</button>
                      </div>
                    </div>
                  </div>
                  <label className="exw-opt" style={{ padding: '12px 0 0' }}>
                    <input type="checkbox" checked={totals} onChange={(e) => setTotals(e.target.checked)} />
                    <span>Add a TOTAL row (sums COD, collected, duties, weight…)</span>
                  </label>
                  {estimate.count > 150000 && format === 'excel' && (
                    <div className="exw-alert warn"><i className="fa fa-info-circle" /> {fmtNum(estimate.count)} rows is a lot for Excel in a browser - CSV is faster and smaller.</div>
                  )}
                  {phase === 'cancelled' && <div className="exw-alert warn"><i className="fa fa-pause-circle" /> Export stopped. Nothing was downloaded - start again whenever you are ready.</div>}
                </div>
              ) : (
                <div className="exw-card">
                  <div className="exw-row" style={{ justifyContent: 'space-between', marginBottom: 10 }}>
                    <b style={{ fontSize: 14 }}>
                      {phase === 'running'
                        ? <><i className="fa fa-spinner fa-spin" style={{ marginRight: 8, color: '#2563eb' }} />Fetching orders…</>
                        : result?.failedDays?.length
                          ? <><i className="fa fa-exclamation-triangle" style={{ marginRight: 8, color: '#d97706' }} />Finished with gaps</>
                          : <><i className="fa fa-check-circle" style={{ marginRight: 8, color: '#16a34a' }} />Done</>}
                    </b>
                    <span style={{ fontSize: 12.5, color: '#64748b' }}>{pct}%</span>
                  </div>
                  <div className="exw-bar"><i style={{ width: `${phase === 'done' ? 100 : pct}%` }} /></div>
                  <div className="exw-stats">
                    <div className="exw-stat"><b>{fmtNum(phase === 'done' ? totalRows : rowsSoFar)}</b><span>orders{estimate.count != null ? ` of ~${fmtNum(estimate.count)}` : ''}</span></div>
                    <div className="exw-stat"><b>{progress?.daysDone ?? 0}/{progress?.daysTotal ?? days.length}</b><span>days loaded</span></div>
                    <div className="exw-stat"><b>{rate ? `${fmtNum(Math.round(rate))}/s` : '—'}</b><span>speed</span></div>
                    <div className="exw-stat"><b>{phase === 'running' ? fmtDuration(etaMs) : fmtDuration(elapsed)}</b><span>{phase === 'running' ? 'remaining' : 'took'}</span></div>
                  </div>
                  {phase === 'running' && progress?.activeDays?.length > 0 && (
                    <div className="exw-hint">Now loading {progress.activeDays.join(', ')}{progress.failed ? ` · ${progress.failed} day(s) failed so far` : ''}</div>
                  )}
                  {phase === 'done' && !result?.failedDays?.length && (
                    <div className="exw-alert ok"><i className="fa fa-check" /> {fmtNum(totalRows)} orders exported to <b>{fileName}.{format === 'csv' ? 'csv' : 'xlsx'}</b>. Check your downloads.</div>
                  )}
                  {phase === 'done' && result?.failedDays?.length > 0 && (
                    <div className="exw-alert warn">
                      <b>{result.failedDays.length} day(s) could not be loaded</b>, so the file would be missing them:
                      <div style={{ margin: '6px 0' }}>{result.failedDays.map((f) => f.day).join(', ')}</div>
                      <div style={{ fontSize: 12 }}>{result.failedDays[0]?.error}</div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="exw-foot">
          <div>
            {step > 1 && phase !== 'running' && (
              <button type="button" className="exw-btn" onClick={() => setStep(step - 1)}><i className="fa fa-arrow-left" /> Back</button>
            )}
          </div>
          <div className="exw-row">
            {step === 1 && <button type="button" className="exw-btn primary" disabled={!canNext1} onClick={() => setStep(2)}>Choose columns <i className="fa fa-arrow-right" /></button>}
            {step === 2 && <button type="button" className="exw-btn primary" disabled={!canExport} onClick={() => setStep(3)}>Continue <i className="fa fa-arrow-right" /></button>}
            {step === 3 && (phase === 'idle' || phase === 'cancelled') && (
              <button type="button" className="exw-btn primary" disabled={!canExport || estimate.count === 0} onClick={() => run(days, null)}>
                <i className="fa fa-download" /> Export {estimate.count != null ? fmtNum(estimate.count) : ''} orders
              </button>
            )}
            {step === 3 && phase === 'running' && <button type="button" className="exw-btn danger" onClick={cancel}><i className="fa fa-stop" /> Stop</button>}
            {step === 3 && phase === 'done' && result?.failedDays?.length > 0 && (
              <>
                <button type="button" className="exw-btn" onClick={() => saveFile(result.rowsByDay, true)}>Download anyway (incomplete)</button>
                <button type="button" className="exw-btn primary" onClick={() => run(result.failedDays.map((f) => f.day), result)}><i className="fa fa-refresh" /> Retry failed days</button>
              </>
            )}
            {step === 3 && phase === 'done' && !result?.failedDays?.length && (
              <>
                <button type="button" className="exw-btn" onClick={() => { setPhase('idle'); setResult(null); }}>New export</button>
                <button type="button" className="exw-btn primary" onClick={() => saveFile(result.rowsByDay, false)}><i className="fa fa-download" /> Download again</button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
